import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function callAIEndpoint(type, prompt, context) {
  const endpoint = type === 'bullet'
    ? `${API_URL}/api/ai/generate-bullet-point`
    : `${API_URL}/api/ai/generate-summary`;
  const { data } = await axios.post(endpoint, { prompt, context }, { withCredentials: true });
  return data.text;
}

async function callATSEndpoint(resumeData, jobDescription) {
  const { data } = await axios.post(`${API_URL}/api/ai/ats-score`, {
    resume_data: resumeData,
    job_description: jobDescription
  }, { withCredentials: true });
  return data;
}

export function useAIAssistant(setWorkExperience, setProfessionalSummary) {
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiType, setAiType] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiContext, setAiContext] = useState(null);

  const [atsDialogOpen, setAtsDialogOpen] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [atsResult, setAtsResult] = useState(null);
  const [atsLoading, setAtsLoading] = useState(false);

  const handleAIGenerate = useCallback(async () => {
    setAiLoading(true);
    try {
      const text = await callAIEndpoint(aiType, aiPrompt, aiContext);
      if (aiType === 'bullet' && aiContext?.workIndex !== undefined) {
        setWorkExperience((prev) => prev.map((w, i) => i === aiContext.workIndex ? { ...w, bullets: [...(w.bullets || []), text] } : w));
      } else if (aiType === 'summary') {
        setProfessionalSummary(text);
      }
      setAiDialogOpen(false);
      setAiPrompt('');
      toast.success('AI content generated!');
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setAiLoading(false);
    }
  }, [aiType, aiPrompt, aiContext, setWorkExperience, setProfessionalSummary]);

  const handleATSCheck = useCallback(async (resumeData) => {
    setAtsLoading(true);
    try {
      const result = await callATSEndpoint(resumeData, jobDescription);
      setAtsResult(result);
    } catch (error) {
      toast.error('Failed to calculate ATS score');
    } finally {
      setAtsLoading(false);
    }
  }, [jobDescription]);

  const openSummaryAI = useCallback((context) => {
    setAiType('summary');
    setAiContext(context);
    setAiPrompt('Generate a professional summary based on my experience');
    setAiDialogOpen(true);
  }, []);

  const openBulletAI = useCallback((workIndex, work) => {
    setAiType('bullet');
    setAiContext({ workIndex });
    setAiPrompt(`Generate a strong bullet point for ${work?.title || 'this role'} at ${work?.company || 'this company'}`);
    setAiDialogOpen(true);
  }, []);

  return {
    aiLoading, aiDialogOpen, setAiDialogOpen, aiPrompt, setAiPrompt,
    handleAIGenerate, openSummaryAI, openBulletAI,
    atsDialogOpen, setAtsDialogOpen, jobDescription, setJobDescription,
    atsResult, atsLoading, handleATSCheck,
  };
}
